import React, { useEffect, useRef } from "react";

/* The right-hand column, without the content.
 *
 * Two panels now live in this slot — a project's documents and one scoring
 * area's requirements — and everything OUTSIDE their content is identical:
 * the width animation, the overlay behaviour below lg, Escape, and returning
 * focus to whatever opened it. Extracted rather than copied, because a second
 * copy of animation timing and focus handling is a second copy that drifts, and
 * the drift shows up as a panel that closes differently depending on which one
 * you opened.
 */

/* Supported panel widths, spelled out as literal class strings.
 *
 * NOT `lg:w-[${width}px]`. Tailwind emits only the class names it can SEE in
 * source, so an interpolated one never reaches the stylesheet and the panel
 * silently collapses to zero. A map costs an entry per width and cannot fail
 * that way — and the two widths here are the two the product actually has: a
 * list panel, and a document that needs a readable measure.
 *
 * The three places must agree. `space` animates the layout gap, `panel` sizes
 * the panel itself at lg, and `overlay` sizes it below lg where it floats over
 * the conversation — a viewport cap keeps a wide panel from covering the whole
 * screen on a phone.
 */
const WIDTHS = {
  400: { space: "lg:w-[400px]", panel: "lg:w-[400px]", overlay: "w-[min(400px,88vw)]" },
  620: { space: "lg:w-[620px]", panel: "lg:w-[620px]", overlay: "w-[min(620px,92vw)]" },
};

const CopilotSidePanel = ({ visible, label, onClose, width = 400, children }) => {
  // An unsupported width would otherwise render a panel with no width at all.
  const size = WIDTHS[width] || WIDTHS[400];
  const panelRef = useRef(null);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    returnFocusRef.current = document.activeElement;
    // Focus the panel itself rather than the close button — landing on "Close"
    // invites closing what you just opened.
    const t = setTimeout(() => panelRef.current?.focus(), 60);
    return () => {
      clearTimeout(t);
      const el = returnFocusRef.current;
      if (el && typeof el.focus === "function") el.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /* Clicking away closes it.
   *
   * On mousedown rather than click: a click that STARTS inside the panel and
   * ends outside (a drag to select a filename, a scrollbar grab) is not a
   * click away, and using `click` would close the panel out from under it.
   *
   * The composer is exempt. It is the one control people use constantly with
   * the panel deliberately open, and closing it every time someone types would
   * make the panel feel broken rather than tidy. Openers are exempt too, so a
   * click that switches panels is not first read as a click that closes one.
   */
  useEffect(() => {
    if (!visible) return undefined;
    const onDown = (e) => {
      const el = panelRef.current;
      if (!el || el.contains(e.target)) return;
      if (e.target.closest?.("[data-sensa-keep-panel]")) return;
      onClose?.();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [visible, onClose]);

  return (
    <>
      {/* Below lg the panel floats over the conversation, so it needs a way out
          by tapping away from it. Above lg it is a column and there is nothing
          to dismiss. */}
      <div
        className={`fixed inset-0 z-30 bg-[#18101C]/20 transition-opacity duration-300 motion-reduce:transition-none lg:hidden ${
          visible ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* The panel's SPACE, animated separately from its CONTENT.
       *
       * Collapsing by toggling `hidden` cannot be animated at all — display is
       * not an animatable property. Animating the panel's own width instead
       * would squash its contents and reflow every row as it opened. So the
       * wrapper's width animates while the panel inside keeps its own fixed
       * width and is simply revealed by overflow — same duration and curve as
       * the history rail, so the space one gives up is the space the other
       * takes. */}
      <div
        className={`h-full w-0 flex-none overflow-hidden transition-[width] duration-300 ease-sidebar motion-reduce:transition-none ${
          visible ? size.space : "lg:w-0"
        }`}
      >
        <aside
          ref={panelRef}
          tabIndex={-1}
          aria-label={label}
          // `relative` is load-bearing: on lg this becomes `static`, and a
          // panel that is not a positioning context lets any absolutely
          // positioned child escape to an ancestor. overflow-hidden keeps a
          // blurred backdrop inside the panel instead of bleeding over the chat.
          className={`fixed inset-y-0 right-0 z-40 flex h-full ${size.overlay} flex-col overflow-hidden border-l border-[#18101C]/[0.07] bg-[#FCFBFC] shadow-[-8px_0_24px_-12px_rgba(24,16,28,0.25)] transition-transform duration-300 ease-sidebar focus:outline-none motion-reduce:transition-none lg:static lg:z-auto ${size.panel} lg:flex-none lg:translate-x-0 lg:shadow-none lg:relative ${
            visible ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {children}
        </aside>
      </div>
    </>
  );
};

export default CopilotSidePanel;
